import { Button } from '@ksyos/design-system';
import { every, isNull, values } from 'lodash';
import { lichamelijkOnderzoekEcgCustomControlDataSchema } from '@common/validation/customControls/cardiology/holterConsult/cardiology';
import type { ValidationErrors } from '../../../../../utils/validation';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { FormInlineGroup, FormParagraph, InputTriState, NullableInputText } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { Modal } from '../../../../elements/modals/Modal';
import { AnamneseType } from './AnamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface ILichamelijkOnderzoekAddEditModalProps {
    anamneseData: ILichamelijkOnderzoek | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item?: ILichamelijkOnderzoek) => Promise<void>;
}

interface ILichamelijkOnderzoekAddEditModalState {
    anamneseData: ILichamelijkOnderzoek;
    errors: ValidationErrors;
}

export interface ILichamelijkOnderzoek {
    diastolisch_geruis: boolean | null;
    extra_tonen: boolean | null;
    hartafwijkingen: boolean | null;
    overig: string | null;
    pericardwrijven: boolean | null;
    systolisch_geruis: boolean | null;
}

export class LichamelijkOnderzoekAddEditModal extends Component<
    ILichamelijkOnderzoekAddEditModalProps,
    ILichamelijkOnderzoekAddEditModalState
> {
    constructor(props: ILichamelijkOnderzoekAddEditModalProps) {
        super(props);
        this.state = {
            anamneseData: {
                hartafwijkingen: null,
                systolisch_geruis: null,
                diastolisch_geruis: null,
                extra_tonen: null,
                pericardwrijven: null,
                overig: null,

                ...this.props.anamneseData,
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        onClick={async () => await this.props.deleteAnamneseData()}
                        variant="outline"
                    >
                        Verwijder
                    </Button>,
                    <Button
                        onClick={async () => await this.validateAnamneseData()}
                        variant="solid"
                    >
                        Anamnese opslaan
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title="Lichamelijk onderzoek"
            >
                <AnamneseWrapper anamneseType={AnamneseType.lichamelijkOnderzoek}>
                    <HorizontalFormGroup hasErrors={this.state.errors.has('hartafwijkingen')}>
                        <FormLabel>Hartafwijkingen</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hartafwijkingen')}
                                name="hartafwijkingen"
                                onChange={async (value) => await this.handleChange('hartafwijkingen', value)}
                                onDoValidate={async () => await this.validateOne('hartafwijkingen')}
                                value={this.state.anamneseData.hartafwijkingen}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('systolisch_geruis')}>
                        <FormLabel>Systolisch geruis</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('systolisch_geruis')}
                                name="systolisch_geruis"
                                onChange={async (value) => await this.handleChange('systolisch_geruis', value)}
                                onDoValidate={async () => await this.validateOne('systolisch_geruis')}
                                value={this.state.anamneseData.systolisch_geruis}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('diastolisch_geruis')}>
                        <FormLabel>Diastolisch geruis</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('diastolisch_geruis')}
                                name="diastolisch_geruis"
                                onChange={async (value) => await this.handleChange('diastolisch_geruis', value)}
                                onDoValidate={async () => await this.validateOne('diastolisch_geruis')}
                                value={this.state.anamneseData.diastolisch_geruis}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('extra_tonen')}>
                        <FormLabel>Extra tonen</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('extra_tonen')}
                                name="extra_tonen"
                                onChange={async (value) => await this.handleChange('extra_tonen', value)}
                                onDoValidate={async () => await this.validateOne('extra_tonen')}
                                value={this.state.anamneseData.extra_tonen}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('pericardwrijven')}>
                        <FormLabel>Pericardwrijven</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('pericardwrijven')}
                                name="pericardwrijven"
                                onChange={async (value) => await this.handleChange('pericardwrijven', value)}
                                onDoValidate={async () => await this.validateOne('pericardwrijven')}
                                value={this.state.anamneseData.pericardwrijven}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('overig')}>
                        <FormLabel>Overig</FormLabel>
                        <InputContainer>
                            <NullableInputText
                                error={this.state.errors.get('overig')}
                                onChange={async (value) => await this.handleChange('overig', value)}
                                onDoValidate={async () => await this.validateOne('overig')}
                                value={this.state.anamneseData.overig}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </AnamneseWrapper>
            </Modal>
        );
    }

    private async handleChange<K extends keyof ILichamelijkOnderzoek>(name: K, value: ILichamelijkOnderzoek[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof ILichamelijkOnderzoek) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(
                lichamelijkOnderzoekEcgCustomControlDataSchema,
                anamneseData,
                errors,
                name
            ),
        }));
    }

    private async validateAnamneseData() {
        if (every(values(this.state.anamneseData), isNull)) {
            return;
        }

        const { hasErrors, errors, item } = validate(
            lichamelijkOnderzoekEcgCustomControlDataSchema,
            this.state.anamneseData
        );
        await this.setState({ errors });
        if (!hasErrors) {
            await this.props.setAnamneseData(item);
        }
    }
}

export function LichamelijkOnderzoekRead(props: ILichamelijkOnderzoek) {
    return (
        <HorizontalFormGroupRead>
            <FormLabel>Lichamelijk onderzoek</FormLabel>
            <InputContainer>
                <FormParagraph>
                    {props.hartafwijkingen !== null && (
                        <FormInlineGroup
                            label="Hartafwijkingen"
                            value={props.hartafwijkingen}
                        />
                    )}
                    {props.systolisch_geruis !== null && (
                        <FormInlineGroup
                            label="Systolisch geruis"
                            value={props.systolisch_geruis}
                        />
                    )}
                    {props.diastolisch_geruis !== null && (
                        <FormInlineGroup
                            label="Diastolisch geruis"
                            value={props.diastolisch_geruis}
                        />
                    )}
                    {props.extra_tonen !== null && (
                        <FormInlineGroup
                            label="Extra tonen"
                            value={props.extra_tonen}
                        />
                    )}
                    {props.pericardwrijven !== null && (
                        <FormInlineGroup
                            label="Pericardwrijven"
                            value={props.pericardwrijven}
                        />
                    )}
                    {props.overig && (
                        <FormInlineGroup
                            label="Overig"
                            value={props.overig}
                        />
                    )}
                </FormParagraph>
            </InputContainer>
        </HorizontalFormGroupRead>
    );
}
